import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo, useState } from 'react';
import type { KeyBindingMap } from 'tinykeys';
import { useGlobalHotkeys } from '@/hooks/useGlobalHotkeys';
import type { UIMediaItem } from '@/types/ui-media';
import { mediaLogger } from './logger';
import { MEDIA_KEY_BINDINGS } from './types';
interface UseMediaViewerStateParameters {
  mediaItems: UIMediaItem[];
  onViewerStateChange?: (isActive: boolean) => void;
}
interface MediaViewerStateResult {
  closeViewer: () => void;
  goToNext: () => void;
  goToPrevious: () => void;
  inlineVideoIndex: number | null;
  isDialogOpen: boolean;
  openPhoto: (index: number) => void;
  playInlineVideo: (index: number) => void;
  selectedIndex: number | undefined;
  setVideoReady: (ready: boolean) => void;
  stopInlineVideo: () => void;
  videoReady: boolean;
}
export function useMediaViewerState(t0) {
  const $ = _c(33);
  const {
    mediaItems,
    onViewerStateChange
  } = t0;
  const [selectedIndex, setSelectedIndex] = useState();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [inlineVideoIndex, setInlineVideoIndex] = useState(null);
  const [videoReady, setVideoReady] = useState(false);
  let t1;
  if ($[0] !== onViewerStateChange) {
    t1 = () => {
      setInlineVideoIndex(null);
      setVideoReady(false);
      onViewerStateChange?.(false);
    };
    $[0] = onViewerStateChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const stopInlineVideo = t1;
  let t2;
  if ($[2] !== onViewerStateChange) {
    t2 = () => {
      setIsDialogOpen(false);
      onViewerStateChange?.(false);
      setTimeout(() => {
        setSelectedIndex(undefined);
        setInlineVideoIndex(null);
        setVideoReady(false);
      }, 50);
    };
    $[2] = onViewerStateChange;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const closeViewer = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setSelectedIndex(previous => {
        if (previous === undefined || previous <= 0) {
          return previous;
        }
        setVideoReady(false);
        return previous - 1;
      });
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const goToPrevious = t3;
  let t4;
  if ($[5] !== mediaItems.length) {
    t4 = () => {
      setSelectedIndex(previous_0 => {
        if (previous_0 === undefined || previous_0 >= mediaItems.length - 1) {
          return previous_0;
        }
        setVideoReady(false);
        return previous_0 + 1;
      });
    };
    $[5] = mediaItems.length;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const goToNext = t4;
  let t5;
  if ($[7] !== mediaItems || $[8] !== onViewerStateChange) {
    t5 = index => {
      if (!mediaItems[index]) {
        return;
      }
      mediaLogger.debug("TweetMedia - Opening photo modal", {
        index,
        mediaType: mediaItems[index]?.type
      });
      onViewerStateChange?.(true);
      setSelectedIndex(index);
      setInlineVideoIndex(null);
      setVideoReady(false);
      setIsDialogOpen(true);
    };
    $[7] = mediaItems;
    $[8] = onViewerStateChange;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const openPhoto = t5;
  let t6;
  if ($[10] !== mediaItems || $[11] !== onViewerStateChange) {
    t6 = index_0 => {
      if (!mediaItems[index_0]) {
        return;
      }
      mediaLogger.debug("TweetMedia - Playing inline video", {
        index: index_0,
        mediaType: mediaItems[index_0]?.type
      });
      onViewerStateChange?.(true);
      setInlineVideoIndex(index_0);
      setIsDialogOpen(false);
      setSelectedIndex(undefined);
      setVideoReady(false);
    };
    $[10] = mediaItems;
    $[11] = onViewerStateChange;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const playInlineVideo = t6;
  let t7;
  if ($[13] !== closeViewer) {
    t7 = event => {
      event.preventDefault();
      closeViewer();
    };
    $[13] = closeViewer;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = event_0 => {
      event_0.preventDefault();
      goToPrevious();
    };
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== goToNext) {
    t9 = event_1 => {
      event_1.preventDefault();
      goToNext();
    };
    $[16] = goToNext;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t7 || $[19] !== t9) {
    t10 = {
      [MEDIA_KEY_BINDINGS.ESCAPE]: t7,
      [MEDIA_KEY_BINDINGS.PREVIOUS]: t8,
      [MEDIA_KEY_BINDINGS.NEXT]: t9
    };
    $[18] = t7;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const bindings = t10;
  let t11;
  if ($[21] !== isDialogOpen) {
    t11 = {
      enabled: isDialogOpen
    };
    $[21] = isDialogOpen;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  useGlobalHotkeys(bindings, t11);
  let t12;
  if ($[23] !== closeViewer || $[24] !== goToNext || $[25] !== inlineVideoIndex || $[26] !== isDialogOpen || $[27] !== openPhoto || $[28] !== playInlineVideo || $[29] !== selectedIndex || $[30] !== stopInlineVideo || $[31] !== videoReady) {
    t12 = {
      selectedIndex,
      isDialogOpen,
      inlineVideoIndex,
      videoReady,
      openPhoto,
      playInlineVideo,
      stopInlineVideo,
      closeViewer,
      goToPrevious,
      goToNext,
      setVideoReady
    };
    $[23] = closeViewer;
    $[24] = goToNext;
    $[25] = inlineVideoIndex;
    $[26] = isDialogOpen;
    $[27] = openPhoto;
    $[28] = playInlineVideo;
    $[29] = selectedIndex;
    $[30] = stopInlineVideo;
    $[31] = videoReady;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  return t12;
}